import React from 'react';
import { Avatar, SxProps, Theme } from '@mui/material';
import { User } from '../types/user';

interface UserAvatarProps {
  user?: Partial<User> | null;
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
  email?: string;
  sx?: SxProps<Theme>;
  size?: number;
}

function stringToColor(string: string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

function stringAvatar(name: string, size?: number) {
  const nameParts = name.split(' ').filter(Boolean);
  const firstInitial = nameParts[0]?.[0] || '';
  const secondInitial = nameParts[1]?.[0] || '';
  const initials = (firstInitial + secondInitial).toUpperCase();

  return {
    sx: {
      bgcolor: stringToColor(name),
      width: size,
      height: size,
      fontSize: size ? size * 0.5 : undefined,
    },
    children: initials,
  };
}

const UserAvatar: React.FC<UserAvatarProps> = ({ user, firstName, lastName, photoUrl, email, sx, size }) => {
  const fName = user?.firstName || firstName || '';
  const lName = user?.lastName || lastName || '';
  const pPhoto = user?.photoUrl || photoUrl;
  const pEmail = user?.email || email || '';

  // Determine display name for hash generation
  const displayName = (fName && lName) ? `${fName} ${lName}` : (pEmail || 'User');

  if (pPhoto) {
    return (
      <Avatar
        alt={displayName}
        src={pPhoto}
        sx={[
          ...(Array.isArray(sx) ? sx : [sx]),
          (size !== undefined && size !== null) ? { width: size, height: size } : {}
        ]}
      />
    );
  }

  const avatarProps = stringAvatar(displayName, size);

  return (
    <Avatar
      {...avatarProps}
      sx={[
        avatarProps.sx,
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    />
  );
};

export default UserAvatar;
